import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft,
    Heart,
    Eye,
    MessageCircle,
    Users,
    User,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    Infinity,
    Sparkles,
    Shield,
    Volume2,
    Copy,
    Check,
    RefreshCw,
    Home,
    BookOpen
} from 'lucide-react';
import { IconButton, PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { InteractiveCycleVisual } from '../components/cycle/InteractiveCycleVisual';
import { EmotionReferenceGuide, EMOTION_DATA } from '../components/cycle/EmotionReferenceGuide';
import { CycleBreakingScripts } from '../components/cycle/CycleBreakingScripts';
import { AdminEditButton } from '../components/admin/AdminEditButton';

/**
 * Break the Cycle - Relationship Cycle Navigator
 * 
 * A guided, step-by-step tool that helps users identify and break 
 * negative relationship patterns by exploring "below the surface" emotions.
 * 
 * Based on EFT (Emotionally Focused Therapy) principles:
 * - The cycle is the problem, not either partner
 * - Surface behaviors protect deeper, vulnerable emotions
 * - Breaking the cycle requires seeing patterns, sharing needs, and mutual soothing
 */

const BreakTheCycle = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState('intro');
    const [mode, setMode] = useState(null); // 'solo' or 'together'
    const [role, setRole] = useState(null); // 'pursuer' or 'withdrawer'
    const [selectedEmotions, setSelectedEmotions] = useState([]);
    const [identifiedNeed, setIdentifiedNeed] = useState('');
    const [identifiedFear, setIdentifiedFear] = useState('');
    const [expandedEmotion, setExpandedEmotion] = useState(null);
    const [copiedScript, setCopiedScript] = useState(null);
    const [hasPartner, setHasPartner] = useState(false); // Check if user has connected partner

    // Check if user has a connected partner
    useEffect(() => {
        const partnerConnected = localStorage.getItem('partner_connected') === 'true';
        setHasPartner(partnerConnected);
    }, []);

    const steps = [
        { id: 'intro', label: 'Welcome' },
        { id: 'mode-select', label: 'Choose Mode' },
        { id: 'see-pattern', label: 'See Your Pattern' },
        { id: 'identify-role', label: 'Your Role' },
        { id: 'discover-underneath', label: 'What\'s Underneath' },
        { id: 'identify-needs', label: 'Your Needs' },
        { id: 'prepare-share', label: 'Prepare to Share' },
        { id: 'regulate-together', label: 'Regulate Together' },
        { id: 'complete', label: 'Complete' }
    ];

    const getCurrentStepIndex = () => steps.findIndex(s => s.id === step);

    const handleBack = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex > 0) {
            setStep(steps[currentIndex - 1].id);
        } else {
            navigate('/relationship-hub');
        }
    };

    const handleNext = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex < steps.length - 1) {
            setStep(steps[currentIndex + 1].id);
        }
    };

    const handleEmotionSelect = (emotionId) => {
        setSelectedEmotions(prev => {
            if (prev.includes(emotionId)) {
                return prev.filter(id => id !== emotionId);
            }
            if (prev.length >= 3) {
                return [...prev.slice(1), emotionId];
            }
            return [...prev, emotionId];
        });
    };

    const copyToClipboard = (text, scriptId) => {
        navigator.clipboard.writeText(text);
        setCopiedScript(scriptId);
        setTimeout(() => setCopiedScript(null), 2000);
    };

    // Get selected emotion details
    const selectedEmotionDetails = selectedEmotions.map(id => 
        EMOTION_DATA.find(e => e.id === id)
    ).filter(Boolean);

    // Derive needs and fears from selected emotions
    const derivedNeeds = [...new Set(selectedEmotionDetails.flatMap(e => e.underlyingNeed.split(', ')))];
    const derivedFears = [...new Set(selectedEmotionDetails.flatMap(e => e.underlyingFear.split(', ')))];

    return (
        <div className="min-h-screen bg-transparent">
            {/* Admin Edit Button */}
            <AdminEditButton toolType="cycle_script" />
            
            {/* Module Header */}
            <ModuleHeader
                title="Break the Cycle"
                onBack={handleBack}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* Progress Indicator */}
                {step !== 'intro' && step !== 'complete' && (
                    <div className="flex justify-center gap-1.5 mb-8">
                        {steps.slice(1, -1).map((s, i) => (
                            <div 
                                key={s.id}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                    i <= getCurrentStepIndex() - 1 
                                        ? 'w-6 bg-[var(--orenda-soft-teal)]' 
                                        : 'w-1.5 bg-[var(--orenda-border)]'
                                }`}
                            />
                        ))}
                    </div>
                )}

                {/* INTRO */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-6">
                                <Infinity className="w-8 h-8" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-4">
                                Break the Cycle
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)] leading-relaxed max-w-md mx-auto">
                                Relationships can get stuck in loops where both people feel hurt or misunderstood. 
                                This tool helps you see what's happening underneath — and find your way back to each other.
                            </BodyText>
                        </div>

                        <div className="glass-card-rose rounded-2xl p-5 mb-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                    style={{ background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' }}>
                                    <Heart className="w-5 h-5" />
                                </div>
                                <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed">
                                    <strong>The cycle is the problem — not either of you.</strong> Both partners are usually 
                                    trying to protect something precious in their own way.
                                </BodyText>
                            </div>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--orenda-soft-teal)]/10">
                                <Eye className="w-5 h-5 text-[#1D6B6B]" />
                                <SmallText className="text-[var(--orenda-text-secondary)]">
                                    See your pattern in real time
                                </SmallText>
                            </div>
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--orenda-blush-rose)]/10">
                                <Heart className="w-5 h-5 text-[#FFBB98]" />
                                <SmallText className="text-[var(--orenda-text-secondary)]">
                                    Discover what's underneath
                                </SmallText>
                            </div>
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <MessageCircle className="w-5 h-5 text-[#7D8E95]" />
                                <SmallText className="text-[var(--orenda-text-secondary)]">
                                    Learn what to say to reconnect
                                </SmallText>
                            </div>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'intro' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('mode-select')}
                            className="w-full"
                            size="action"
                            data-testid="start-cycle-btn"
                        >
                            Begin
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* MODE SELECT */}
                {step === 'mode-select' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-3">
                                How would you like to explore?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                You can do this alone or with your partner.
                            </BodyText>
                        </div>

                        <div className="space-y-4 mb-8">
                            <button
                                className={`w-full text-left rounded-2xl p-5 glass-card-sage card-hover transition-all ${mode === 'solo' ? 'ring-2 ring-[var(--orenda-soft-teal)]' : ''}`}
                                onClick={() => setMode('solo')}
                                data-testid="mode-solo"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' }}>
                                        <User className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-1">
                                            Solo reflection
                                        </Heading>
                                        <SmallText className="text-[var(--orenda-text-secondary)]">
                                            Explore your part in the cycle privately. You can share with your partner later if you choose.
                                        </SmallText>
                                    </div>
                                </div>
                            </button>

                            <button
                                className={`w-full text-left rounded-2xl p-5 glass-card card-hover transition-all ${mode === 'together' ? 'ring-2 ring-[var(--orenda-soft-teal)]' : ''} ${!hasPartner ? 'opacity-60' : ''}`}
                                onClick={() => hasPartner && setMode('together')}
                                disabled={!hasPartner}
                                data-testid="mode-together"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)', color: 'white' }}>
                                        <Users className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-1">
                                            Together with partner
                                        </Heading>
                                        <SmallText className="text-[var(--orenda-text-secondary)]">
                                            {hasPartner 
                                                ? "Go through this together, taking turns to share and listen."
                                                : "Connect with your partner first to use this mode."
                                            }
                                        </SmallText>
                                        {!hasPartner && (
                                            <div className="mt-2 flex items-center gap-1.5">
                                                <Sparkles className="w-3.5 h-3.5 text-[#7D8E95]" />
                                                <SmallText className="text-[#7D8E95] text-xs">
                                                    Partner connection required
                                                </SmallText>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </button>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'mode-select' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('see-pattern')}
                            className="w-full"
                            size="action"
                            disabled={!mode}
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* SEE YOUR PATTERN - Interactive Cycle Visual */}
                {step === 'see-pattern' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <Heading as="h2" size="text-xl" className="mb-3">
                                See Your Pattern
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto">
                                This is how negative cycles work. Tap each part to learn more.
                            </BodyText>
                        </div>

                        <InteractiveCycleVisual className="mb-8" />

                        <div className="glass-card-gold rounded-2xl p-5 mb-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                    style={{ background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' }}>
                                    <Shield className="w-5 h-5" />
                                </div>
                                <div>
                                    <SmallText className="text-[var(--orenda-text-primary)] font-medium block mb-1">
                                        Own your part in the cycle
                                    </SmallText>
                                    <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                        Nothing can change when you villainize your partner. 
                                        See the cycle as the problem — and enlist their help in interrupting it.
                                    </SmallText>
                                </div>
                            </div>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'see-pattern' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('identify-role')}
                            className="w-full"
                            size="action"
                        >
                            I understand
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* IDENTIFY ROLE */}
                {step === 'identify-role' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-3">
                                Which role do you tend to take?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                Neither is wrong. This is just how you've learned to protect yourself.
                            </BodyText>
                        </div>

                        <div className="space-y-4 mb-8">
                            <button
                                className={`w-full text-left rounded-2xl p-5 glass-card-rose card-hover transition-all ${role === 'pursuer' ? 'ring-2 ring-[var(--orenda-soft-teal)]' : ''}`}
                                onClick={() => setRole('pursuer')}
                                data-testid="role-pursuer"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' }}>
                                        <span className="text-xl">→</span>
                                    </div>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-1">
                                            The Pursuer
                                        </Heading>
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            I tend to reach out, seek answers, or try to talk things through. 
                                            I may come across as demanding or anxious.
                                        </SmallText>
                                        <div className="mt-3 p-3 rounded-lg bg-white/50">
                                            <SmallText className="text-[#E09060] italic">
                                                Underneath: longing for connection, fear of abandonment, need for reassurance
                                            </SmallText>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            <button
                                className={`w-full text-left rounded-2xl p-5 glass-card-sage card-hover transition-all ${role === 'withdrawer' ? 'ring-2 ring-[var(--orenda-soft-teal)]' : ''}`}
                                onClick={() => setRole('withdrawer')}
                                data-testid="role-withdrawer"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' }}>
                                        <span className="text-xl">←</span>
                                    </div>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-1">
                                            The Withdrawer
                                        </Heading>
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            I tend to step back, go quiet, or need space when things feel intense. 
                                            I may seem distant or shut down.
                                        </SmallText>
                                        <div className="mt-3 p-3 rounded-lg bg-white/50">
                                            <SmallText className="text-[#1D6B6B] italic">
                                                Underneath: overwhelm, fear of failing, need for space to think
                                            </SmallText>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            <button
                                onClick={() => setRole('both')}
                                className={`w-full p-4 rounded-xl text-center transition-all glass-card ${
                                    role === 'both' 
                                        ? 'ring-2 ring-[var(--orenda-soft-teal)]' 
                                        : ''
                                }`}
                            >
                                <SmallText className="text-[var(--orenda-text-secondary)]">
                                    I switch between both depending on the situation
                                </SmallText>
                            </button>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'identify-role' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('discover-underneath')}
                            className="w-full"
                            size="action"
                            disabled={!role}
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* DISCOVER WHAT'S UNDERNEATH - Emotion Reference Guide */}
                {step === 'discover-underneath' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <Heading as="h2" size="text-xl" className="mb-3">
                                What's underneath?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto">
                                Select up to 3 emotions that resonate with how you feel during conflict.
                            </BodyText>
                        </div>

                        <EmotionReferenceGuide
                            selectedEmotions={selectedEmotions}
                            onEmotionSelect={handleEmotionSelect}
                            expandedEmotion={expandedEmotion}
                            onExpandEmotion={setExpandedEmotion}
                            className="mb-6"
                        />

                        {selectedEmotions.length > 0 && (
                            <div className="glass-card rounded-2xl p-5 mb-6">
                                <SmallText className="text-[var(--orenda-text-muted)] font-medium block mb-3">
                                    What you've selected:
                                </SmallText>
                                <div className="flex flex-wrap gap-2">
                                    {selectedEmotionDetails.map(emotion => (
                                        <span 
                                            key={emotion.id}
                                            className="px-3 py-1.5 rounded-full text-sm font-medium"
                                            style={{ 
                                                backgroundColor: `${emotion.color}20`,
                                                color: emotion.color
                                            }}
                                        >
                                            {emotion.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'discover-underneath' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('identify-needs')}
                            className="w-full"
                            size="action"
                            disabled={selectedEmotions.length === 0}
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* IDENTIFY NEEDS & FEARS */}
                {step === 'identify-needs' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-xl" className="mb-3">
                                Your needs and fears
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                Based on what you shared, here's what might be underneath.
                            </BodyText>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-5 mb-4">
                            <SmallText className="text-[var(--orenda-soft-teal)] font-semibold block mb-3">
                                What you might really need:
                            </SmallText>
                            <div className="space-y-2">
                                {derivedNeeds.slice(0, 4).map((need, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[var(--orenda-soft-teal)] mt-2" />
                                        <BodyText className="text-[var(--orenda-text-primary)]">
                                            {need}
                                        </BodyText>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="glass-card-rose rounded-2xl p-5 mb-6">
                            <SmallText className="text-[var(--orenda-blush-rose)] font-semibold block mb-3">
                                What you might be afraid of:
                            </SmallText>
                            <div className="space-y-2">
                                {derivedFears.slice(0, 4).map((fear, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[var(--orenda-blush-rose)] mt-2" />
                                        <BodyText className="text-[var(--orenda-text-primary)]">
                                            {fear}
                                        </BodyText>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20">
                            <SmallText className="text-[var(--orenda-text-secondary)] text-center italic">
                                Sharing these softer feelings — instead of the harder, protective ones — 
                                is often what helps couples reconnect.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'identify-needs' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('prepare-share')}
                            className="w-full"
                            size="action"
                        >
                            Learn what to say
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* PREPARE TO SHARE - Scripts */}
                {step === 'prepare-share' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-4">
                                <MessageCircle className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-xl" className="mb-3">
                                What to say
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                When you see you're in the cycle, try saying something like this.
                            </BodyText>
                        </div>

                        <CycleBreakingScripts 
                            role={role}
                            onCopy={copyToClipboard}
                            copiedScript={copiedScript}
                            className="mb-6"
                        />

                        <div className="glass-card-gold rounded-2xl p-5 mb-6">
                            <SmallText className="text-[var(--orenda-text-secondary)] text-center leading-relaxed">
                                You don't have to use these exact words. What matters is shifting from 
                                <strong> protecting yourself</strong> to <strong>protecting your relationship</strong>.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'prepare-share' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('regulate-together')}
                            className="w-full"
                            size="action"
                        >
                            Continue to Regulate Together
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* REGULATE TOGETHER */}
                {step === 'regulate-together' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Users className="w-8 h-8" />
                            </IconContainer>
                            <Heading as="h2" size="text-xl" className="mb-3">
                                Regulate Together
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                The final step: meet the need, soothe the fear.
                            </BodyText>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div className="glass-card-sage rounded-2xl p-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' }}>
                                        <SmallText className="font-bold">1</SmallText>
                                    </div>
                                    <div>
                                        <BodyText className="text-[var(--orenda-text-primary)] font-medium mb-1">
                                            Share with your partner
                                        </BodyText>
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            Share your softer feelings and what you really need. 
                                            Use the scripts if it helps.
                                        </SmallText>
                                    </div>
                                </div>
                            </div>

                            <div className="glass-card-rose rounded-2xl p-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' }}>
                                        <SmallText className="font-bold">2</SmallText>
                                    </div>
                                    <div>
                                        <BodyText className="text-[var(--orenda-text-primary)] font-medium mb-1">
                                            Really listen
                                        </BodyText>
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            Hear the story of love and fear where you used to hear criticism. 
                                            Resist the urge to defend — just listen.
                                        </SmallText>
                                    </div>
                                </div>
                            </div>

                            <div className="glass-card rounded-2xl p-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                                        style={{ background: 'linear-gradient(145deg, #9AABB8 0%, #7D8E95 100%)', color: 'white' }}>
                                        <SmallText className="font-bold">3</SmallText>
                                    </div>
                                    <div>
                                        <BodyText className="text-[var(--orenda-text-primary)] font-medium mb-1">
                                            Meet the need, soothe the fear
                                        </BodyText>
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            Once you both feel heard, share what would help. 
                                            Then offer what your partner needs.
                                        </SmallText>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 rounded-2xl bg-[var(--orenda-soft-teal)]/10 border border-[var(--orenda-soft-teal)]/20">
                            <div className="flex items-start gap-3">
                                <Heart className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[var(--orenda-text-primary)] leading-relaxed italic">
                                    "Show me how to be your safety."
                                </SmallText>
                            </div>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'regulate-together' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('complete')}
                            className="w-full"
                            size="action"
                        >
                            Complete
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* COMPLETE */}
                {step === 'complete' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--orenda-soft-teal)]/20 flex items-center justify-center">
                                <Heart className="w-10 h-10 text-[#1D6B6B]" />
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-4">
                                Well done
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto leading-relaxed">
                                Taking the time to understand your patterns is a powerful step 
                                toward creating a safer, more connected relationship.
                            </BodyText>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed italic">
                                "When couples can recognise the cycle and understand the softer emotions underneath, 
                                they often rediscover the connection they've been fighting for all along."
                            </BodyText>
                        </div>

                        <div className="text-center mb-8">
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                You can return to this tool anytime. Take what helps, leave what doesn't.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="extended" />
                    </div>
                )}

                {step === 'complete' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full"
                            size="action"
                        >
                            <Home className="w-4 h-4 mr-1.5" />
                            Return to Hub
                        </PrimaryButton>
                        <SecondaryButton
                            onClick={() => {
                                setStep('intro');
                                setMode(null);
                                setRole(null);
                                setSelectedEmotions([]);
                            }}
                            className="w-full"
                            size="action"
                        >
                            <RefreshCw className="w-4 h-4 mr-1.5" />
                            Start again
                        </SecondaryButton>
                    </PersistentBottomBar>
                )}
            </main>
        </div>
    );
};

export default BreakTheCycle;
